import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * SignaturePad — pressure-sensitive digital ink. Line thickness varies by pointer
 * pressure (stylus) or by speed (mouse/finger fallback: slower = thicker).
 * Exposes getDataURL() (base64 PNG, transparent bg) and clear() via ref.
 */
const SignaturePad = forwardRef(({ height = 160, label = "Sign here" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#3a2a4a";
  }, [height]);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, t: Date.now(),
             pressure: e.pressure && e.pressure > 0 ? e.pressure : null };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    const l = last.current;
    let width;
    if (p.pressure != null) {
      width = 1 + p.pressure * 6; // stylus pressure
    } else {
      const dist = Math.hypot(p.x - l.x, p.y - l.y);
      const dt = Math.max(1, p.t - l.t);
      const speed = dist / dt; // px/ms
      width = Math.max(1.2, Math.min(6, 5 - speed * 3)); // slower = thicker
    }
    ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(l.x, l.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };

  const end = () => { drawing.current = false; last.current = null; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => !hasInk,
    getDataURL: () => (hasInk ? canvasRef.current.toDataURL("image/png").split(",")[1] : null),
  }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        data-testid="signature-pad"
        style={{ height, touchAction: "none" }}
        className="w-full rounded-2xl bg-white border border-border cursor-crosshair"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      {!hasInk && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground/60 text-sm font-serif italic">
          {label}
        </div>
      )}
      <button type="button" data-testid="signature-clear" onClick={clear}
        className="absolute bottom-2 right-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground bg-white/80 rounded-lg px-2 py-1">
        <Eraser className="w-3.5 h-3.5" /> Clear
      </button>
    </div>
  );
});

SignaturePad.displayName = "SignaturePad";
export default SignaturePad;
